import React, { useState, useEffect, useRef } from 'react';
import { FaPencilAlt, FaImage, FaCheckCircle, FaBolt, FaUpload, FaTimes, FaTh, FaBars } from 'react-icons/fa';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import SummaryApi from "../common/index";
import GridView from '../component/admincomponent/GridView';
import ListView from '../component/admincomponent/ListView';

const CategoryManagement = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    category_name: '',
    category_image: null,
    is_trendy_course: false,
    is_career_course: false,
      is_degree_category: false, 
    is_active: false,
  });
  const [imagePreview, setImagePreview] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [zoomedImage, setZoomedImage] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [viewMode, setViewMode] = useState('grid');
  const [searchQuery, setSearchQuery] = useState("");
  const [isDarkMode, setIsDarkMode] = useState(false);
  const fileInputRef = useRef();

  // Theme detection - follow admin-theme from localStorage and listen for changes
  useEffect(() => {
    const updateTheme = () => {
      const adminTheme = sessionStorage.getItem('admin-theme');
      const shouldBeDark = adminTheme === 'dark';
      setIsDarkMode(shouldBeDark);
      document.documentElement.classList.toggle('dark', shouldBeDark);
    };

    // Initial theme setup
    updateTheme();

    // Listen for theme changes from AdminPanel
    const handleThemeChange = () => {
      updateTheme();
    };

    // Listen for storage changes
    const handleStorageChange = (e) => {
      if (e.key === 'admin-theme') {
        updateTheme();
      }
    };

    // Listen for custom admin theme events
    const handleAdminThemeToggled = () => {
      updateTheme();
    };

    window.addEventListener('themeChanged', handleThemeChange);
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('adminThemeToggled', handleAdminThemeToggled);

    return () => {
      window.removeEventListener('themeChanged', handleThemeChange);
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('adminThemeToggled', handleAdminThemeToggled);
    };
  }, []);

  const formatImageUrl = (imagePath) => {
    if (!imagePath) return '';
    if (imagePath.startsWith('http')) {
      return imagePath;
    }
    if (imagePath instanceof File) {
      return URL.createObjectURL(imagePath);
    }
    const baseUrl = SummaryApi.Images.url.replace('/api', '');
    if (imagePath.includes(baseUrl)) {
      return imagePath;
    }
    if (imagePath.startsWith('uploads/')) {
      return `${baseUrl}/${imagePath}`;
    }
    return `${baseUrl}/${imagePath}`;
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = () => {
    setLoading(true);
    fetch(SummaryApi.CategoryList.url, { method: SummaryApi.CategoryList.method })
      .then((res) => res.json())
      .then((data) => {
        if (data.status && Array.isArray(data.data)) {
          const updatedCategories = data.data.map((category) => ({
            ...category,
            category_image: formatImageUrl(category.category_image),
            is_trendy_course: category.is_trendy_course === 1,
            is_career_course: category.is_career_course === 1,
            is_active: category.is_active === 1,
            is_degree_category: category.is_degree_category === 1,  // ⭐ ADD THIS

          }));
          setCategories(updatedCategories);
        } else {
          setCategories([]);
        }
      })
      .catch((err) => {
        console.error("Error fetching categories:", err);
        toast.error("Failed to fetch categories");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
        setFormData({
          ...formData,
          category_image: file,
        });
      };
      reader.readAsDataURL(file);
    }
  };

  const removeImage = () => {
    setImagePreview(null);
    setFormData((prev) => ({
      ...prev,
      category_image: null,
    }));
  };

  const toggleActive = () => {
    setFormData((prev) => ({
      ...prev,
      is_active: !prev.is_active,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const submitData = new FormData();
    submitData.append('category_name', formData.category_name);
    submitData.append('is_trendy_course', formData.is_trendy_course ? 1 : 0);
    submitData.append('is_career_course', formData.is_career_course ? 1 : 0);
    submitData.append('is_active', formData.is_active ? 1 : 0);
    submitData.append('is_degree_category', formData.is_degree_category ? 1 : 0);  // ⭐ ADD THIS

    if (formData.category_image instanceof File) {
      submitData.append('category_image', formData.category_image);
    } else if (typeof formData.category_image === 'string' && formData.category_image) {
      const filename = formData.category_image.split('/').pop();
      submitData.append('category_image', filename);
    }
    if (editingId) {
      submitData.append('category_id', editingId);
      fetch(SummaryApi.Categoryupdate.url, {
        method: SummaryApi.Categoryupdate.method,
        body: submitData,
      })
        .then((res) => res.json())
        .then((data) => {
          if (data.status) {
            toast.success("Category updated successfully!");
            resetForm();
            fetchCategories();
            setIsModalOpen(false);
          } else {
            console.error("Update failed:", data.message);
            toast.error(data.message || "Failed to update category");
          }
        })
        .catch((err) => {
          console.error("Error updating category:", err);
          toast.error("Error updating category");
        });
    } else {
      fetch(SummaryApi.Categorycreate.url, {
        method: SummaryApi.Categorycreate.method,
        body: submitData,
      })
        .then((res) => res.json())
        .then((data) => {
          if (data.status) {
            toast.success("Category created successfully!");
            resetForm();
            fetchCategories();
            setIsModalOpen(false);
          } else {
            console.error("Creation failed:", data.message);
            toast.error(data.message || "Failed to create category");
          }
        })
        .catch((err) => {
          console.error("Error creating category:", err);
          toast.error("Error creating category");
        });
    }
  };

  const handleEdit = (category) => {
    setFormData({
      category_name: category.category_name,
      category_image: category.category_image,
      is_trendy_course: category.is_trendy_course,
      is_career_course: category.is_career_course,
      is_active: category.is_active,
      is_degree_category: category.is_degree_category,   // ⭐ ADD THIS

    });
    setEditingId(category.category_id);
    if (category.category_image) {
      setImagePreview(category.category_image);
    }
    setIsModalOpen(true);
  };

  const resetForm = () => {
    setFormData({
      category_name: '',
      category_image: null,
      is_trendy_course: false,
      is_career_course: false,
      is_active: false,
    });
    setImagePreview(null);
    setEditingId(null);
  };

  const handleImageClick = (imageUrl) => {
    setZoomedImage(imageUrl);
  };

  const closeZoomedImage = () => {
    setZoomedImage(null);
  };

  const triggerFileInput = () => {
    fileInputRef.current.click();
  };

  const openCreateModal = () => {
    resetForm();
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    resetForm();
  };

  const toggleViewMode = (mode) => {
    setViewMode(mode);
  };

const renderGridItem = (category) => (
  <div className={`p-4 rounded-lg transition-all duration-300 hover:shadow-lg ${
    isDarkMode 
      ? 'bg-gray-800 border-2 border-gray-600 hover:border-blue-400 hover:bg-gray-700' 
      : 'bg-white hover:bg-blue-50'
  }`}>
    <div className="flex justify-between items-center mb-1">
      <h3 className={`text-sm font-semibold line-clamp-2 transition-colors duration-300 ${
        isDarkMode ? 'text-white' : 'text-gray-800'
      }`}>{category.category_name}</h3>
      <button
        onClick={() => handleEdit(category)}
        className={`p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors duration-200`}
        aria-label={`Edit ${category.category_name}`}
        title={`Edit ${category.category_name}`}
      >
        <FaPencilAlt className={`w-4 h-4 transition-colors duration-300 ${
          isDarkMode ? 'text-blue-400' : 'text-blue-900'
        }`} />
      </button>
    </div>
    <div className="mb-2">
      {category.category_image ? (
        <img
          src={category.category_image}
          alt={category.category_name}
          className="h-20 w-full object-contain rounded-md cursor-pointer"
          onClick={() => handleImageClick(category.category_image)}
        />
      ) : (
        <FaImage className={`h-20 w-full transition-colors duration-300 ${
          isDarkMode ? 'text-blue-400' : 'text-blue-900'
        }`} />
      )}
    </div>
    <div className="text-xs text-gray-600 dark:text-gray-400 flex flex-wrap gap-1 select-none justify-start">
      <span
        className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
          category.is_trendy_course 
            ? 'bg-green-900 text-green-200' 
            : 'bg-red-900 text-red-200'
        }`}
      >
        Trendy: {category.is_trendy_course ? 'Yes' : 'No'}
      </span>
      <span
        className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
          category.is_career_course 
            ? 'bg-green-900 text-green-200' 
            : 'bg-red-900 text-red-200'
        }`}
      >
        Career: {category.is_career_course ? 'Yes' : 'No'}
      </span>
      <span
        className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
          category.is_degree_category 
            ? 'bg-green-900 text-green-200' 
            : 'bg-red-900 text-red-200'
        }`}
      >
        Degree: {category.is_degree_category ? 'Yes' : 'No'}
      </span>
      <span
        className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
          category.is_active 
            ? 'bg-green-900 text-green-200' 
            : 'bg-red-900 text-red-200'
        }`}
      >
        Active: {category.is_active ? 'Yes' : 'No'}
      </span>
    </div>
  </div>
);


  const renderListHeader = () => (
    <>
      <th className={`border px-3 py-2 font-semibold text-center align-middle transition-colors duration-300 ${
        isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-gray-50 text-gray-800 border-gray-300'
      }`}>Category</th>
      <th className={`border px-3 py-2 font-semibold text-center align-middle transition-colors duration-300 ${
        isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-gray-50 text-gray-800 border-gray-300'
      }`}>Image</th>
      <th className={`border px-3 py-2 font-semibold text-center align-middle transition-colors duration-300 ${
        isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-gray-50 text-gray-800 border-gray-300'
      }`}>Status</th>
      <th className={`border px-3 py-2 font-semibold text-center align-middle transition-colors duration-300 ${
        isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-gray-50 text-gray-800 border-gray-300'
      }`}>Actions</th>
    </>
  );

  const renderListRow = (category) => (
    <>
      <td
        className={`border px-3 py-2 max-w-xs text-center align-middle transition-colors duration-300 ${
          isDarkMode ? 'bg-gray-800 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-800'
        }`}
        title={category.category_name}
      >
        <div className="line-clamp-2">{category.category_name}</div>
      </td>
      <td className={`border px-3 py-2 text-center align-middle transition-colors duration-300 ${
        isDarkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-300'
      }`}>
        {category.category_image ? (
          <img
            src={category.category_image}
            alt={category.category_name}
            className="h-12 w-12 object-contain rounded cursor-pointer mx-auto"
            onClick={() => handleImageClick(category.category_image)}
          />
        ) : (
          <FaImage className={`h-12 w-12 mx-auto transition-colors duration-300 ${
            isDarkMode ? 'text-blue-400' : 'text-blue-900'
          }`} />
        )}
      </td>
      <td className={`border px-3 py-2 text-center align-middle transition-colors duration-300 ${
        isDarkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-300'
      }`}>
        <div className="flex flex-col sm:flex-row flex-wrap justify-center items-center gap-2 mx-auto">
          <span
            className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
              category.is_trendy_course 
                ? "bg-green-900 text-green-200" 
                : "bg-red-900 text-red-200"
            }`}
          >
            Trendy: {category.is_trendy_course ? "Yes" : "No"}
          </span>
          <span
            className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
              category.is_career_course 
                ? "bg-green-900 text-green-200" 
                : "bg-red-900 text-red-200"
            }`}
          >
            Career: {category.is_career_course ? "Yes" : "No"}
          </span>
          <span
            className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
              category.is_career_course 
                ? "bg-green-900 text-green-200" 
                : "bg-red-900 text-red-200"
            }`}
          >
            Degree: {category.is_degree_category ? "Yes" : "No"}
          </span>
          <span
            className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
              category.is_active 
                ? "bg-green-900 text-green-200" 
                : "bg-red-900 text-red-200"
            }`}
          >
            Active: {category.is_active ? "Yes" : "No"}
          </span>
        </div>
      </td>
      <td className={`border px-3 py-2 text-center align-middle transition-colors duration-300 ${
        isDarkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-300'
      }`}>
        <button
          onClick={() => handleEdit(category)}
          className={`p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200`}
          aria-label={`Edit ${category.category_name}`}
          title={`Edit ${category.category_name}`}
        >
          <FaPencilAlt className={`w-4 h-4 transition-colors duration-300 ${
            isDarkMode ? 'text-blue-400' : 'text-blue-900'
          }`} />
        </button>
      </td>
    </>
  );

  const filteredCategories = categories.filter((cat) =>
    cat.category_name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className={`min-h-screen p-4 sm:p-6 transition-colors duration-300 ${
      isDarkMode ? 'bg-gray-900' : 'bg-white'
    }`}>
      {zoomedImage && (
        <div
          className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50"
          onClick={closeZoomedImage}
        >
          <div className="max-w-4xl max-h-full p-4">
            <img
              src={zoomedImage}
              alt="Zoomed"
              className="max-w-full max-h-full object-contain"
            />
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto w-full flex flex-col flex-grow">
        {/* Header / Controls */}
        <div className="mb-6 sm:mb-8 flex flex-col sm:flex-row sm:justify-between sm:items-center flex-shrink-0 gap-3">
          <h1 className={`text-2xl sm:text-3xl font-bold transition-colors duration-300 ${
            isDarkMode ? 'text-white' : 'text-gray-800'
          }`}>Category Management</h1>
          
          <div className="flex flex-wrap items-center gap-3">
            <input
              type="text"
              placeholder="Search categories..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={`border rounded-md px-3 py-3 focus:ring-2 focus:ring-blue-500 transition-all duration-200 ${
                isDarkMode 
                  ? 'bg-gray-800 border-gray-600 text-white focus:border-blue-500 placeholder-gray-400' 
                  : 'border-gray-300 focus:border-blue-500 placeholder-gray-500'
              } w-60`}
            />
            <button
              onClick={() => toggleViewMode('grid')}
              aria-label="Grid view"
              className={`p-2 rounded-md font-medium border transition-colors duration-300 ${
                viewMode === 'grid'
                  ? 'bg-blue-900 text-white border-blue-900'
                  : isDarkMode 
                    ? 'text-blue-400 border-blue-400 hover:bg-blue-400 hover:text-white'
                    : 'text-blue-900 border-blue-900 hover:bg-blue-600 hover:text-white'
              }`}
              title="Grid View"
              type="button"
            >
              <FaTh />
            </button>
            <button
              onClick={() => toggleViewMode('list')}
              aria-label="List view"
              className={`p-2 rounded-md font-medium border transition-colors duration-300 ${
                viewMode === 'list'
                  ? 'bg-blue-900 text-white border-blue-900'
                  : isDarkMode 
                    ? 'text-blue-400 border-blue-400 hover:bg-blue-400 hover:text-white'
                    : 'text-blue-900 border-blue-900 hover:bg-blue-600 hover:text-white'
              }`}
              title="List View"
              type="button"
            >
              <FaBars />
            </button>
           
            <button
              onClick={openCreateModal}
              className={`px-4 py-3 rounded-md text-white font-medium transition-colors duration-200 ${
                isDarkMode 
                  ? 'bg-blue-900 hover:bg-blue-700 shadow-lg shadow-blue-600/30' 
                  : 'bg-blue-900 hover:bg-blue-800 shadow-lg shadow-blue-700/30'
              }`}
            >
              Add New Category
            </button>
          </div>
        </div>

        {/* Modal */}
        {isModalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className={`rounded-xl shadow-lg p-5 sm:p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto border transition-colors duration-300 ${
              isDarkMode ? 'bg-gray-800 border-gray-600 shadow-gray-900/50' : 'bg-white border-gray-200'
            }`}>
              <div className="flex justify-between items-center mb-4">
                <h2 className={`text-lg sm:text-xl font-semibold transition-colors duration-300 ${
                  isDarkMode ? 'text-white' : 'text-gray-800'
                }`}>
                  {editingId ? 'Update Category' : 'Create New Category'}
                </h2>
                <button onClick={closeModal} className={`transition-colors duration-300 ${
                  isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'
                }`}>
                  <FaTimes className="w-6 h-6" />
                </button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className={`block text-sm font-medium mb-1 transition-colors duration-300 ${
                      isDarkMode ? 'text-gray-300' : 'text-gray-700'
                    }`}>Category Name</label>
                    <input
                      type="text"
                      name="category_name"
                      value={formData.category_name}
                      onChange={handleChange}
                      className={`block w-full border rounded-md p-2 focus:ring-2 focus:ring-blue-500 transition-all duration-200 ${
                        isDarkMode 
                          ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400 focus:border-blue-500' 
                          : 'border-gray-300 focus:border-blue-500 placeholder-gray-500'
                      }`}
                      placeholder="Enter category name"
                      required
                    />
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-1 transition-colors duration-300 ${
                      isDarkMode ? 'text-gray-300' : 'text-gray-700'
                    }`}>Category Image</label>
                    {imagePreview || formData.category_image ? (
                      <div className="relative mt-1 w-28 h-28">
                        <img
                          src={imagePreview || formatImageUrl(formData.category_image)}
                          alt="Category preview"
                          className="w-full h-full object-contain border rounded-md cursor-pointer"
                          onClick={triggerFileInput}
                        />
                        <button
                          type="button"
                          onClick={removeImage}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors duration-200"
                        >
                          <FaTimes className="w-3 h-3" />
                        </button>
                      </div>
                    ) : (
                      <label
                        className={`flex flex-col items-center justify-center w-full h-20 border-2 border-dashed rounded-md cursor-pointer transition-colors duration-300 ${
                          isDarkMode 
                            ? 'border-gray-600 hover:border-blue-500 bg-gray-700 hover:bg-gray-600' 
                            : 'border-gray-300 hover:border-blue-500 hover:bg-gray-50'
                        }`}
                        onClick={triggerFileInput}
                      >
                        <div className="flex flex-col items-center justify-center pt-5 pb-6">
                          <FaUpload className={`w-8 h-8 mb-3 transition-colors duration-300 ${
                            isDarkMode ? 'text-gray-400' : 'text-gray-400'
                          }`} />
                          <p className={`mb-2 text-sm transition-colors duration-300 ${
                            isDarkMode ? 'text-gray-400' : 'text-gray-500'
                          }`}>Click to upload</p>
                        </div>
                      </label>
                    )}
                    <input
                      type="file"
                      className="hidden"
                      onChange={handleImageChange}
                      accept="image/*"
                      ref={fileInputRef}
                    />
                  </div>
                  <div className="flex flex-col space-y-4 mt-4 md:mt-0 col-span-2">
                    <div className="flex flex-wrap items-center gap-8">
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          name="is_trendy_course"
                          checked={formData.is_trendy_course}
                          onChange={handleChange}
                          className="sr-only peer"
                        />
                        <div
                          className={`w-11 h-6 ${
                            formData.is_trendy_course ? 'bg-green-500' : 'bg-red-500'
                          } peer-focus:outline-none rounded-full peer after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all ${
                            formData.is_trendy_course ? 'peer-checked:after:translate-x-full' : ''
                          }`}
                        />
                        <span className={`ml-3 text-sm font-medium flex items-center transition-colors duration-300 ${
                          isDarkMode ? 'text-gray-300' : 'text-gray-900'
                        }`}>
                          <FaBolt className={`mr-2 transition-colors duration-300 ${
                            isDarkMode ? 'text-blue-400' : 'text-blue-900'
                          }`} /> Trendy Course
                        </span>
                      </label>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          name="is_career_course"
                          checked={formData.is_career_course}
                          onChange={handleChange}
                          className="sr-only peer"
                        />
                        <div
                          className={`w-11 h-6 ${
                            formData.is_career_course ? 'bg-green-500' : 'bg-red-500'
                          } peer-focus:outline-none rounded-full peer after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all ${
                            formData.is_career_course ? 'peer-checked:after:translate-x-full' : ''
                          }`}
                        />
                        <span className={`ml-3 text-sm font-medium flex items-center transition-colors duration-300 ${
                          isDarkMode ? 'text-gray-300' : 'text-gray-900'
                        }`}>
                          <FaCheckCircle className={`mr-2 transition-colors duration-300 ${
                            isDarkMode ? 'text-blue-400' : 'text-blue-900'
                          }`} /> Career Course
                        </span>
                      </label>
                      <label className="relative inline-flex items-center cursor-pointer">
  <input
    type="checkbox"
    name="is_degree_category"
    checked={formData.is_degree_category}
    onChange={handleChange}
    className="sr-only peer"
  />
  <div
    className={`w-11 h-6 ${
      formData.is_degree_category ? 'bg-green-500' : 'bg-red-500'
    } peer-focus:outline-none rounded-full peer
      after:content-[''] after:absolute after:top-[2px] after:left-[2px]
      after:bg-white after:border-gray-300 after:border after:rounded-full
      after:h-5 after:w-5 after:transition-all ${
        formData.is_degree_category ? 'peer-checked:after:translate-x-full' : ''
      }`}
  />
  <span
    className={`ml-3 text-sm font-medium flex items-center transition-colors duration-300 ${
      isDarkMode ? 'text-gray-300' : 'text-gray-900'
    }`}
  >
    Degree Category
  </span>
</label>

                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          name="is_active"
                          checked={formData.is_active}
                          onChange={toggleActive}
                          className="sr-only peer"
                        />
                        <div
                          className={`w-11 h-6 ${
                            formData.is_active ? 'bg-green-500' : 'bg-red-500'
                          } peer-focus:outline-none rounded-full peer after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all ${
                            formData.is_active ? 'peer-checked:after:translate-x-full' : ''
                          }`}
                        />
                        <span className={`ml-3 text-sm font-medium transition-colors duration-300 ${
                          isDarkMode ? 'text-gray-300' : 'text-gray-900'
                        }`}>Active</span>
                      </label>
                    </div>
                  </div>
                </div>
                <div className="mt-6 flex justify-end gap-2">
                  <button
                    type="submit"
                    className={`px-4 py-3 rounded-md text-white font-medium transition-colors duration-200 ${
                      isDarkMode 
                        ? 'bg-blue-600 hover:bg-blue-700 shadow-lg shadow-blue-600/30' 
                        : 'bg-blue-900 hover:bg-blue-800 shadow-lg shadow-blue-700/30'
                    }`}
                  >
                    {editingId ? 'Update Category' : 'Create Category'}
                  </button>
                  <button
                    type="button"
                    onClick={closeModal}
                    className={`px-4 py-3 rounded-md font-medium transition-colors duration-200 ${
                      isDarkMode 
                        ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' 
                        : 'bg-gray-500 text-white hover:bg-gray-600'
                    }`}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Categories List */}
        <div className={`rounded-xl shadow-lg overflow-hidden border flex flex-col flex-grow transition-colors duration-300 ${
          isDarkMode ? 'bg-gray-800 border-gray-700 shadow-gray-900/50' : 'bg-white border-gray-200'
        }`}>
          <div className={`px-4 py-5 sm:px-6 border-b flex justify-between items-center flex-shrink-0 transition-colors duration-300 ${
            isDarkMode 
              ? 'border-gray-700 bg-gray-800' 
              : 'border-gray-200 bg-white'
          }`}>
            <h2 className={`text-lg sm:text-xl font-semibold transition-colors duration-300 ${
              isDarkMode ? 'text-white' : 'text-gray-800'
            }`}>Categories List</h2>
          </div>

          <div className="p-3 flex-grow" style={{ minHeight: '400px' }}>
            {viewMode === 'grid' ? (
              <GridView 
                data={filteredCategories}
                loading={loading}
                renderItem={renderGridItem}
                columns={{ sm: 2, md: 3, lg: 4 }}
                noDataMessage="No Categories Found"
                noDataSubMessage="Get started by creating your first category"
              />
            ) : (
              <ListView 
                data={filteredCategories}
                loading={loading}
                renderHeader={renderListHeader}
                renderRow={renderListRow}
                noDataMessage="No Categories Found"
                noDataSubMessage="Get started by creating your first category"
              />
            )}
          </div>
        </div>
      </div>

      <style>{`
        .line-clamp-2 {
          overflow: hidden;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
        }
      `}</style>
    </div>
  );
};

export default CategoryManagement;